import { useMemo } from 'react'
import { getIntegrationServiceIdsForCategory, IntegrationCategory, ONBOARDING_UNSUPPORTED_INTEGRATIONS } from '../../common/integrations'
import { type CapabilityState } from '../../models/Onboarding'
import { selectAccountId } from '../auth/authSlice'
import { useAppSelector } from '../hooks'
import { useGetMeQuery } from '../user/user'
import { useIntegrations } from '../../views/Integration/useIntegrations'

const PLANNING_SOURCES = getIntegrationServiceIdsForCategory(IntegrationCategory.PLANNING).filter((id) => !ONBOARDING_UNSUPPORTED_INTEGRATIONS.includes(id))

export const useFinancialPlanningCapability = (): CapabilityState & { isLoading: boolean } => {
  const accountId = useAppSelector(selectAccountId)
  const { data: me, isLoading: isMeLoading } = useGetMeQuery({ accountId })
  const { isIntegrationAllowed, isLoading: isIntegrationsLoading } = useIntegrations()

  const connectedIds = useMemo(() => new Set(me?.integrations?.map((i) => i.serviceId) ?? []), [me])

  return useMemo<CapabilityState & { isLoading: boolean }>(() => {
    const allowedServiceIds = PLANNING_SOURCES.filter((id) => isIntegrationAllowed(id))
    const isAllowed = allowedServiceIds.length > 0
    const isFulfilled = PLANNING_SOURCES.some((id) => connectedIds.has(id))
    const connectedIntegrations = PLANNING_SOURCES.filter((id) => connectedIds.has(id))
    return {
      isAllowed,
      isFulfilled,
      allowedServiceIds,
      connectedIntegrations,
      connectedWithoutSync: [],
      isLoading: isMeLoading || isIntegrationsLoading,
    }
  }, [isIntegrationAllowed, connectedIds, isMeLoading, isIntegrationsLoading])
}
